import axios from 'axios';

// Set the base URL for all API requests
const url = `${process.env.REACT_APP_BASE_URL}`;
const instance = axios.create({
  baseURL: url
});

// Function to retrieve the login url
export const userLogin = async () => {
  try {
    const response = await instance.get('/auth/login');
    return response;
  } catch (error) {
    console.error('Error logging in', error);
    throw error;
  }
};

// Function to retrieve the logout url
export const userLogout = async () => {
  try {
    const response = await instance.get('/auth/logout');
    return response;
  } catch (error) {
    console.error('Error logging out', error);
    throw error;
  }
};

// Function to read the user's profile
export const readProfile = async (user_id) => {
  try {
    const response = await instance.get(`/users/${user_id}`, {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error reading profile', error);
    throw error;
  }
};

// Function to fetch all chats
export const fetchChats = async (user_id) => {
  try {
    const response = await instance.get(`/chats/${user_id}`, {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error fetching chats', error);
    throw error;
  }
};

// Function to create new chat
export const createChat = async (user_id, chat_name) => {
  try {
    const response = await instance.post('/chats/', 
      { 
        "user_id": user_id, 
        "chat_name": chat_name
      }, 
      {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error creating chat', error);
    throw error;
  }
};

// Function to delete chat
export const deleteChat = async (user_id, chat_id) => {
  try {
    const response = await instance.delete(`/chats/${chat_id}`, {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error deleting chat', error);
    throw error;
  }
};

export const renameChat = async (user_id, chat_id, chat_name) => {
  try {
    const response = await instance.put(`/chats/${chat_id}`, 
      { 
        "chat_id": chat_id,
        "chat_name": chat_name
      }, 
      {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error renaming chat', error);
    throw error;
  }
};

export default instance;

export const fetchMessages = async (user_id, chat_id) => {
  try {
    const response = await instance.get(`/messages/${chat_id}`, {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error fetching messages', error);
    throw error;
  }
};

export const createMessage = async (user_id, chat_id, role, content) => {
  try {
    const response = await instance.post(`/messages/${chat_id}`, 
      { 
        "role": role,
        "content": content
      }, 
      {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error creating message', error);
    throw error;
  }
};

export const updateMessage = async (user_id, message_id, content) => {
  try {
    const response = await instance.put(`/messages/${message_id}`, 
      { 
        "content": content
      }, 
      {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error updating message', error);
    throw error;
  }
};

export const regenerateButton = async (user_id, message_id) => {
  try {
    const response = await instance.post(`/messages/${message_id}/regenerateButton`, {}, {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error updating regenerate button status', error);
    throw error;
  }
};

export const updateThumbsUp = async (user_id, message_id) => {
  try {
    const response = await instance.post(`/messages/${message_id}/thumbsup`, {}, {
      headers: {
        Authorization: `${user_id}`,
      },
    });
    return response;
  } catch (error) {
    console.error('Error updating thumbs up', error);
    throw error;
  }
};

export const updateThumbsDown = async (user_id, message_id) => {
  try {
    const response = await instance.post(`/messages/${message_id}/thumbsdown`, {}, {
      headers: {
        Authorization: `${user_id}`,
      },
    });
    return response;
  } catch (error) {
    console.error('Error updating thumbs up', error);
    throw error;
  }
};

export const submitFeedback = async (user_id, message_id, message, sources) => {
  try {
    const response = await instance.post(`/messages/${message_id}/feedback`,
      { 
        "message": message, 
        "sources": sources, 
      },
      {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error submitting feedback', error);
    throw error;
  }
};

export const copyMessage = async (user_id, message_id) => {
  try {
    const response = await instance.post(`/messages/${message_id}/copy`, {}, {
      headers: {
        Authorization: `${user_id}`,
      },
    });
    return response;
  } catch (error) {
    console.error('Error updating copy message', error);
    throw error;
  }
};

export const generateMessage = async (user_id, query, model_name, chat_history) => {
  try {
    const response = await fetch(url + '/generate/', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `${user_id}`,
        },
        body: JSON.stringify({ query: query, model_name: model_name, chat_history: chat_history }),
    });

    if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    return reader; 
  } catch (error) {
    console.error('Error generating message', error);
    throw error;
  }
};

export const createSources = async (user_id, message_id, sources) => {
  try {
    const response = await instance.post(`/sources/${message_id}`, 
      { 
        "sources": sources
      }, 
      {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error creating sources', error);
    throw error;
  }
};

export const deleteSources = async (user_id, message_id) => {
  try {
    const response = await instance.delete(`/sources/${message_id}`, {
        headers: {
          Authorization: `${user_id}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error('Error deleting sources', error);
    throw error;
  }
};